/**
 * notification_settings.repository.ts — 상단바 알림 표시 항목 (조직 공통).
 *
 * Firestore: "system_settings/notification_settings" — { signup, sales_sync, rqc_drift, updated_at }
 *   각 항목 = 해당 알림 타입을 상단바 벨에 표시할지 여부 (on/off).
 *
 * 읽기: 모든 활성 사용자 / 쓰기: 관리자만 (firestore.rules system_settings 기본 분기).
 * 미설정 시 전부 ON (DEFAULT_NOTIFICATION_SETTINGS).
 */
import { doc, onSnapshot, setDoc, Timestamp } from "firebase/firestore";
import { db } from "./firebase";
import { tenantSettingsDocPath } from "@/domain/tenantPath";

const DOC_PATH = ["system_settings", "notification_settings"] as const;

/** 알림 타입 키 — useNotifications 의 NotifItem.type 과 1:1 (관리 3종 + 출하 4종) */
export type NotifTypeKey =
  | "signup"
  | "sales_sync"
  | "rqc_drift"
  | "morning_plan"
  | "ship_start"
  | "ship_finish"
  | "ship_eod";

export type NotificationSettings = Record<NotifTypeKey, boolean>;

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  signup: true,
  sales_sync: true,
  rqc_drift: true,
  morning_plan: true,
  ship_start: true,
  ship_finish: true,
  ship_eod: true,
};

function normalize(data: Record<string, unknown> | undefined): NotificationSettings {
  const d = data ?? {};
  // 누락 키는 기본값(ON)으로 채움 — 새 알림 타입 추가 시 기존 문서도 자연히 ON
  return {
    signup: d.signup !== false,
    sales_sync: d.sales_sync !== false,
    rqc_drift: d.rqc_drift !== false,
    morning_plan: d.morning_plan !== false,
    ship_start: d.ship_start !== false,
    ship_finish: d.ship_finish !== false,
    ship_eod: d.ship_eod !== false,
  };
}

export const notificationSettingsRepo = {
  watch(cb: (s: NotificationSettings) => void, onError?: (e: Error) => void): () => void {
    return onSnapshot(
      doc(db, tenantSettingsDocPath(DOC_PATH[1])),
      (snap) => cb(normalize(snap.data())),
      onError,
    );
  },

  async save(settings: NotificationSettings): Promise<void> {
    await setDoc(
      doc(db, tenantSettingsDocPath(DOC_PATH[1])),
      { ...settings, updated_at: Timestamp.now() },
      { merge: true },
    );
  },
};
